import { useState } from 'react';
import classNames from 'classnames';
import {
  Link,
} from 'react-router';
import {
  VibeButton,
} from 'vibeguide';
import {
  FormHelperText,
} from '@mui/material';
import {
  AccountCircle,
  Lock,
} from '@mui/icons-material';
import './LoginForm.scss';

function LoginForm({
  step = 'email',
  username = '',
  password = '',
  loginError = false,
  loginErrorMessage = '',
  cookiesAccepted = false,
  emailReadOnly = false,
  passwordAutoFocus = false,
  onUsernameChange = () => {},
  onPasswordChange = () => {},
  onForgotPassword = () => {},
  onChangeEmail = () => {},
  onSubmit = () => {},
}) {
  const isEmailStep = step === 'email';

  const [isAccountFocus, setIsAccountFocus] = useState(false);
  const [isPasswordFocus, setIsPasswordFocus] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);

  const onKeyDown = (e) => {
    if (!isEmailStep) {
      const capsLockOn = e.getModifierState('CapsLock');

      if (!isCapsLockOn && capsLockOn) {
        setIsCapsLockOn(true);
      } else if (isCapsLockOn && !capsLockOn) {
        setIsCapsLockOn(false);
      }
    }

    if (e.key === 'Enter') {
      onSubmit(e);
    }
  };

  const submitDisabled = isEmailStep
    ? !username
    : password.length <= 0 || !username;

  return (
    <div
      className="LoginForm"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
      }}
    >
      <div
        className="form-title"
        style={{
          fontSize: 16,
          textAlign: 'center',
        }}
      >
        Your retail platform to standardize and unify digital in-store.
      </div>

      <form className="login-container">
        <div
          className={classNames('input-container', {
            active: isAccountFocus,
            'has-error': isEmailStep && loginError,
          })}
        >
          <input
            className="login-input"
            type="email"
            placeholder="Username"
            autoComplete="username"
            value={username}
            onChange={onUsernameChange}
            onKeyDown={onKeyDown}
            onFocus={() => setIsAccountFocus(true)}
            onBlur={() => setIsAccountFocus(false)}
            disabled={!cookiesAccepted}
            readOnly={!isEmailStep && emailReadOnly}
            autoFocus={isEmailStep || (!emailReadOnly && !passwordAutoFocus)}
            data-testid="login-email-input"
          />

          <AccountCircle className="login-icon account-icon" />

          {!isEmailStep && isCapsLockOn && (
            <FormHelperText className="login-capslock">
              CAPSLOCK is on
            </FormHelperText>
          )}

          {isEmailStep && loginError && (
            <FormHelperText className="login-error">
              {loginErrorMessage}
            </FormHelperText>
          )}
        </div>

        <div
          className={classNames('input-container', {
            active: isPasswordFocus,
            'has-error': !isEmailStep && loginError,
            'sr-only': isEmailStep,
          })}
          aria-hidden={isEmailStep}
        >
          <input
            className="login-input"
            type="password"
            placeholder="********"
            autoComplete="current-password"
            value={password}
            onChange={onPasswordChange}
            onKeyDown={onKeyDown}
            onFocus={() => setIsPasswordFocus(true)}
            onBlur={() => setIsPasswordFocus(false)}
            disabled={!cookiesAccepted}
            autoFocus={passwordAutoFocus}
            tabIndex={isEmailStep ? -1 : undefined}
            data-testid="login-password-input"
          />

          <Lock className="login-icon lock-icon" />

          {!isEmailStep && loginError && (
            <FormHelperText className="login-error">
              {loginErrorMessage}
            </FormHelperText>
          )}
        </div>

        {!isEmailStep && emailReadOnly && cookiesAccepted && (
          <button
            type="button"
            className="forgot change-email"
            onClick={onChangeEmail}
            data-testid="login-change-email-button"
          >
            Use a different email
          </button>
        )}

        {!isEmailStep && cookiesAccepted && (
          <Link
            className="forgot"
            to="/forgot"
            onClick={onForgotPassword}
            data-testid="login-forgot-password-link"
          >
            Forgot password
          </Link>
        )}

        {cookiesAccepted && (
          <VibeButton
            style={{
              width: '100%',
              height: 48,
              fontSize: 16,
            }}
            text="Sign in"
            color="primary"
            disabled={submitDisabled}
            testId="login-submit-button"
            onClick={onSubmit}
          />
        )}
      </form>
    </div>
  );
}

export default LoginForm;
